import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  GraduationCap, Terminal, MousePointer, ShieldCheck, Rocket, CheckCircle2, Github, Linkedin,
} from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

const PROMISES = [
  { icon: MousePointer, title: "7 UI-automation levels", body: "From buttons to shadow DOM — every widget ships with a stable data-testid so your scripts run first time." },
  { icon: Terminal, title: "A real API playground", body: "12 curated CRUD endpoints across auth, profile, jobs and applications with copy-paste curl and a Try-it button." },
  { icon: ShieldCheck, title: "Free forever, no strings", body: "No paywall, no signup wall for UI practice. Optional login is only for authenticated API practice." },
  { icon: Rocket, title: "Made for interviewers too", body: "Send the URL of a specific level as a QA interview task — every element is stable and scriptable." },
];

const LEVELS = [
  { name: "Level 1 · Basic", detail: "Buttons, links, inputs, checkboxes, radios, native selects" },
  { name: "Level 2 · Forms", detail: "Textarea, date, file upload, sliders, debounced autocomplete, validation" },
  { name: "Level 3 · Mouse & Keyboard", detail: "Hover, double/right click, drag & drop, keyboard, scrolling" },
  { name: "Level 4 · Complex Components", detail: "Tabs, accordion, modal, tooltip, pagination, carousel, stepper" },
  { name: "Level 5 · Dynamic UI", detail: "AJAX, loading states, infinite scroll, conditional fields, dynamic tables" },
  { name: "Level 6 · Browser Handling", detail: "alerts, popups, new tabs, permissions, downloads" },
  { name: "Level 7 · Advanced Automation", detail: "iframes, Shadow DOM, virtualized tables, custom elements, cookie vs token auth" },
];

export default function About() {
  useDocumentTitle("About — QaTestology practice ground");
  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">about this site</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950">
          A practice ground for QA &amp; automation engineers.
        </h1>
        <p className="mt-4 text-lg text-zinc-700 max-w-3xl leading-relaxed">
          QaTestology is a <strong>demo &amp; practice site</strong> — it is <em>not</em> a real job board. The mock job-seeker
          and employer flows exist purely so learners can test real UI patterns and CRUD APIs the same way they'd meet
          them at work.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {PROMISES.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="p-5">
              <div className="flex items-start gap-3">
                <span className="h-10 w-10 rounded-md bg-blue-50 text-[#002FA7] grid place-items-center flex-shrink-0">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-display font-bold text-zinc-950">{title}</div>
                  <p className="text-sm text-zinc-700 mt-0.5">{body}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mb-4">What you'll practise here</h2>
          <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 bg-white">
            {LEVELS.map((l) => (
              <div key={l.name} className="p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-zinc-950">{l.name}</div>
                  <div className="text-xs tag-mono text-zinc-600">{l.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/practice">
              <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white">
                <GraduationCap className="h-4 w-4 mr-2" /> Start practising
              </Button>
            </Link>
            <Link to="/practice/api">
              <Button variant="outline">
                <Terminal className="h-4 w-4 mr-2" /> Open API playground
              </Button>
            </Link>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mb-3">Who it's for</h2>
          <ul className="text-zinc-700 leading-relaxed space-y-1 list-disc list-inside">
            <li><strong>Aspiring QA engineers</strong> learning Selenium, Playwright, Cypress, or WebdriverIO from scratch.</li>
            <li><strong>SDETs</strong> validating their framework on tricky patterns (Shadow DOM, iframes, virtualised lists).</li>
            <li><strong>API testers</strong> practising CRUD flows, auth headers, and status-code assertions against a real backend.</li>
            <li><strong>Interviewers &amp; mentors</strong> who want a stable, shareable target for scripting exercises.</li>
          </ul>
        </section>

        <hr className="my-14 border-zinc-200" />

        <section data-testid="about-founder-block" className="text-sm text-zinc-600 leading-relaxed">
          <span className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold">maintained by</span>
          <p className="mt-1">
            <strong className="text-zinc-950">Nitesh Kumar</strong> — QA engineer, automation enthusiast. Built QaTestology to give the community a
            no-nonsense sandbox for practising modern testing tools.{" "}
            <a href="https://www.linkedin.com/in/nitesh-kumar-3624b6110/" target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
            </a>
            <span className="text-zinc-400"> · </span>
            <a href="https://github.com" target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
              <Github className="h-3.5 w-3.5" /> GitHub
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}
